import { Component, OnInit, OnDestroy, inject, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HaupiaResponseRowResult, HaupiaResponseRowHighlighting, HaupiaService, getFirstValue, Facet } from '../../providers/haupia.service';
import { combineLatest, Subject } from 'rxjs';
import { PageService, preventScroll } from '../../providers/page.service';
import { debounceTime, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { MappService } from '../../../app/providers/mapp.service';
import { AspectRatio, getLogger, RouterOrExternalLinkDirective } from '@knorr-bremse-portals/ngx-components';
import { BytesPipe } from '@knorr-bremse-portals/ngx-components/pipes/bytes';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { imageToPicture, Picture, PlainImageSection, urlToPicture } from '../plain-image/plain-image.component';
import { SettingsService } from '../../../app/providers/settings.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { iconArrowsRight, iconSearch } from '@knorr-bremse-portals/icons';
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon';
import { HtmlComponent } from '@knorr-bremse-portals/ngx-components/components/html';
import { LineClampComponent } from '@knorr-bremse-portals/ngx-components/components/line-clamp';
import { PictureComponent } from '@knorr-bremse-portals/ngx-components/components/picture';
import { ProgressIndicatorComponent } from '@knorr-bremse-portals/ngx-components/components/progress-indicator';
import { MainGridComponent } from '@knorr-bremse-portals/ngx-components/components/main-grid';
import { ChipSetFilterComponent, FilterChipComponent } from '@knorr-bremse-portals/ngx-components/components/controls/chip-set-filter';
import { InputComponent } from '@knorr-bremse-portals/ngx-components/components/controls/input';
import { ButtonComponent } from '@knorr-bremse-portals/ngx-components/components/button';
import { StandaloneLinkComponent } from '@knorr-bremse-portals/ngx-components/components/standalone-link';
import { GLOBAL_REGION } from '../../providers/settings-constants';
import { Loggers } from '../../../express.tokens';
import { Meta, Title } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';

const PREPARED_SEARCH_NAME_GLOBAL_SEARCH = 'PS_COMMON';
const RESULT_PAGE_SIZE = 5;
const QUERY_PARAM_QUERY = 'query';
const QUERY_PARAM_FILTER_TYPE = 'type';

enum HaupiaResponseType {
  Document = 'document',
  Page = 'page',
  MagazineArticle = 'magazine_article',
  PressRelease = 'press_release',
  Event = 'event',
  Product = 'product',
  CrossReference = 'cross-reference',
  Software = 'software',
  Training = 'training',
}

enum HaupiaResponseSubType {
  InvestorRelations = 'investor-relations',
  Webshop = 'webshop',
}

type DocumentHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.Document];
  abstract: [string];
  fileSize_long: number;
  title: [string];
}

type IrDocumentHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.Document];
  subType_keyword: [HaupiaResponseSubType.InvestorRelations];
  abstract: [string];
  fileSize_long: number;
  fileName: [string];
  title: [string];
}

type PageHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.Page];
  intro?: [string];
  abstract: [string];
  headline?: [string];
  title: [string];
}

type IrPageHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.Page];
  subType_keyword: [HaupiaResponseSubType.InvestorRelations];
  abstract: [string];
  headline?: [string];
  title: [string];
}

type MagazineArticleHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.MagazineArticle];
  abstract: [string];
  headline: [string];
  image_stored_only: [string];
}

type PressReleaseHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.PressRelease];
  abstract: [string];
  headline: [string];
  image_stored_only: [string];
}

type IrPressReleaseHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.PressRelease];
  subType_keyword: [HaupiaResponseSubType.InvestorRelations];
  abstract: [string];
  headline: [string];
}

type EventHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.Event];
  name: [string];
  start_date: string;
  end_date: string;
  abstract: [string];
  image_stored_only: [string];
}

type WebshopProductHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.Product];
  subType_keyword: [HaupiaResponseSubType.Webshop];
  partNumber: [string];
  typeNumber_keyword: [string];
  status_keyword: [string];
  imageUrl_stored_only: [string];
  abstract: [string];
}

type WebshopCrossReferenceHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.CrossReference];
  subType_keyword: [HaupiaResponseSubType.Webshop];
  internalPartNumber: [string];
  crossReferencePartNumber: [string];
  crossReferenceManufacturer_keyword: [string];
  title: [string];
  typeNumber_keyword: [string];
  status_keyword: [string];
  imageUrl_stored_only: [string];
  abstract: [string];
}

type WebshopDocumentHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.Document];
  subType_keyword: [HaupiaResponseSubType.Webshop];
  documentNumber: [string];
  title: [string];
  category_keyword: [string];
  version: [string];
  release_date: string;
  abstract: [string];
}

type WebshopSoftwareHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.Software];
  subType_keyword: [HaupiaResponseSubType.Webshop];
  softwarePackageCode: [string];
  softwarePackageName: [string];
  softwareGroup_keyword: [string];
  softwareType_keyword: [string];
  softwarePackageType_keyword: [string];
  version: [string];
  abstract: [string];
}

type WebshopTrainingHaupiaResponseRowResult = HaupiaResponseRowResult & {
  type_keyword: [HaupiaResponseType.Training];
  subType_keyword: [HaupiaResponseSubType.Webshop];
  abstract: [string];
  title: [string];
  trainingShortCode_keyword: [string];
}

type CommonHaupiaResponseRowResult =
  DocumentHaupiaResponseRowResult | IrDocumentHaupiaResponseRowResult |
  PageHaupiaResponseRowResult | IrPageHaupiaResponseRowResult |
  MagazineArticleHaupiaResponseRowResult | PressReleaseHaupiaResponseRowResult |
  IrPressReleaseHaupiaResponseRowResult |
  EventHaupiaResponseRowResult |
  WebshopProductHaupiaResponseRowResult | WebshopCrossReferenceHaupiaResponseRowResult | WebshopDocumentHaupiaResponseRowResult | WebshopSoftwareHaupiaResponseRowResult | WebshopTrainingHaupiaResponseRowResult
;

function isAnyDocumentSearchResult(result: CommonHaupiaResponseRowResult): result is DocumentHaupiaResponseRowResult | IrDocumentHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.Document;
}

function isDocumentSearchResult(result: CommonHaupiaResponseRowResult): result is DocumentHaupiaResponseRowResult {
  return isAnyDocumentSearchResult(result) && !('subType_keyword' in result);
}

function isIrDocumentSearchResult(result: CommonHaupiaResponseRowResult): result is IrDocumentHaupiaResponseRowResult {
  return isAnyDocumentSearchResult(result) && ('subType_keyword' in result) && result.subType_keyword[0] === HaupiaResponseSubType.InvestorRelations;
}

function isPageSearchResult(result: CommonHaupiaResponseRowResult): result is PageHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.Page && !('subType_keyword' in result);
}

function isIrPageSearchResult(result: CommonHaupiaResponseRowResult): result is IrPageHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.Page && ('subType_keyword' in result) && result.subType_keyword[0] === HaupiaResponseSubType.InvestorRelations;
}

function isMagazineArticleSearchResult(result: CommonHaupiaResponseRowResult): result is MagazineArticleHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.MagazineArticle;
}

function isPressReleaseSearchResult(result: CommonHaupiaResponseRowResult): result is PressReleaseHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.PressRelease && !('subType_keyword' in result);
}

function isIrPressReleaseSearchResult(result: CommonHaupiaResponseRowResult): result is IrPressReleaseHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.PressRelease && ('subType_keyword' in result) && result.subType_keyword[0] === HaupiaResponseSubType.InvestorRelations;
}

function isEventSearchResult(result: CommonHaupiaResponseRowResult): result is EventHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.Event;
}

function isWebshopProductSearchResult(result: CommonHaupiaResponseRowResult): result is WebshopProductHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.Product && ('subType_keyword' in result) && result.subType_keyword[0] === HaupiaResponseSubType.Webshop;
}

function isWebshopCrossReferenceSearchResult(result: CommonHaupiaResponseRowResult): result is WebshopCrossReferenceHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.CrossReference && ('subType_keyword' in result) && result.subType_keyword[0] === HaupiaResponseSubType.Webshop;
}

function isWebshopDocumentSearchResult(result: CommonHaupiaResponseRowResult): result is WebshopDocumentHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.Document && ('subType_keyword' in result) && result.subType_keyword[0] === HaupiaResponseSubType.Webshop;
}

function isWebshopSoftwareSearchResult(result: CommonHaupiaResponseRowResult): result is WebshopSoftwareHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.Software && ('subType_keyword' in result) && result.subType_keyword[0] === HaupiaResponseSubType.Webshop;
}

function isWebshopTrainingSearchResult(result: CommonHaupiaResponseRowResult): result is WebshopTrainingHaupiaResponseRowResult {
  return result.type_keyword[0] === HaupiaResponseType.Training && ('subType_keyword' in result) && result.subType_keyword[0] === HaupiaResponseSubType.Webshop;
}

interface CommonHaupiaResponseRowHighlighting extends HaupiaResponseRowHighlighting {
  content: [string];
  intro: [string];
  abstract: [string];
  title: [string];
  headline: [string];
  name: [string];
  fileName: [string];
}

type CommonSearchHaupiaResponseFacets = {
  type: HaupiaResponseType;
  countryCode: string;
}

enum GlobalSearchResultType {
  File = 'file',
  Page = 'page',
  News = 'news',
  Event = 'event',
  Product = 'product',
  CrossReference = 'cross-reference',
  Software = 'software',
  Training = 'training',
  WebshopDocument = 'webshop-document',
}

interface GlobalSearchResult {
  link: string;
  type: GlobalSearchResultType;
  title: string;
  content: string;
  fileSize?: number;
  picture?: Picture;
  startDate?: Date;
  endDate?: Date;
}

function combineLines(...lines: string[]): string {
  return lines.filter(line => line).join('<br>');
}

function prefixValue(prefix: string, value: string): string {
  if (!value) {
    return '';
  }
  return `${prefix}: ${value}`;
}

@Component({
    selector: 'app-global-search',
    styleUrls: ['./global-search.component.scss'],
    templateUrl: './global-search.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterOrExternalLinkDirective,
        HtmlComponent,
        LineClampComponent,
        PictureComponent,
        ProgressIndicatorComponent,
        IconComponent,
        MainGridComponent,
        ChipSetFilterComponent,
        FilterChipComponent,
        InputComponent,
        BytesPipe,
        ButtonComponent,
        StandaloneLinkComponent,
    ],
})
export class GlobalSearchComponent implements OnInit, OnDestroy {

  private meta = inject(Meta);
  private title = inject(Title);

  protected iconSearch = iconSearch;
  protected iconAllFilter = iconArrowsRight;
  protected iconLoadMore = iconArrowsRight;

  protected resultTypes = GlobalSearchResultType;
  protected filterTypeTranslations = {
    [HaupiaResponseType.Event]: $localize `:global search filter type event@@:Event`,
    [HaupiaResponseType.MagazineArticle]: $localize `:global search filter type magazine article@@:Magazine Article`,
    [HaupiaResponseType.Page]: $localize `:global search filter type page@@:Page`,
    [HaupiaResponseType.Document]: $localize `:global search filter type document@@:Document`,
    [HaupiaResponseType.PressRelease]: $localize `:global search filter type press release@@:Press Release`,
    [HaupiaResponseType.Product]: $localize `:global search filter type product@@:Product`,
    [HaupiaResponseType.CrossReference]: $localize `:global search filter type cross reference@@:Cross Reference`,
    [HaupiaResponseType.Software]: $localize `:global search filter type software@@:Software`,
    [HaupiaResponseType.Training]: $localize `:global search filter type training@@:Training`,
  }

  private platformBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private mappService = inject(MappService);
  private logger = getLogger(Loggers.Renderer);

  protected preparedSearch = inject(HaupiaService).prepareSearch<
    CommonHaupiaResponseRowResult,
    CommonHaupiaResponseRowHighlighting,
    CommonSearchHaupiaResponseFacets
  >(PREPARED_SEARCH_NAME_GLOBAL_SEARCH, RESULT_PAGE_SIZE);

  protected searchForm = new FormGroup({
    query: new FormControl(this.route.snapshot.queryParamMap.get(QUERY_PARAM_QUERY) ?? '', { nonNullable: true }),
    typeFilter: new FormControl<HaupiaResponseType | ''>(this.route.snapshot.queryParamMap.get(QUERY_PARAM_FILTER_TYPE) as HaupiaResponseType ?? '', { nonNullable: true }),
  });

  protected filterTypes$ = this.preparedSearch.results$.pipe(
    map(results => results.facets?.type as Facet<HaupiaResponseType>),
  );

  protected numResults$ = this.preparedSearch.results$.pipe(
    map(results => results.numRows),
  );

  protected loading$ = this.preparedSearch.loading$;

  protected results$ = this.preparedSearch.results$.pipe(
    tap((results) => {
      if (results.numRows === null) {
        return;
      }

      this.mappService.trackInternalSearch(this.searchForm.controls.query.value, results.numRows);
    }),
    map((results) => {
      return results.rows.map((row) => {
        try {
          const result = row.result;
          const highlighting = row.highlighting;

          if (isDocumentSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.File,
              title: getFirstValue(highlighting.title, result.title),
              content: getFirstValue(highlighting.abstract, result.abstract),
              fileSize: result.fileSize_long,
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          if (isIrDocumentSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.File,
              title: getFirstValue(highlighting.title, highlighting.fileName, result.title, result.fileName),
              content: getFirstValue(highlighting.abstract, result.abstract),
              fileSize: result.fileSize_long,
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          if (isPageSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.Page,
              title: getFirstValue(highlighting.headline, highlighting.title, result.headline, result.title),
              content: getFirstValue(highlighting.abstract, highlighting.content, highlighting.intro, result.abstract),
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          if (isIrPageSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.Page,
              title: getFirstValue(highlighting.headline, highlighting.title, result.headline, result.title),
              content: getFirstValue(highlighting.abstract, highlighting.content, result.abstract),
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          if (isMagazineArticleSearchResult(result) || isPressReleaseSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.News,
              title: getFirstValue(highlighting.headline, result.headline),
              content: getFirstValue(highlighting.abstract, highlighting.content, result.abstract),
              picture: imageToPicture((JSON.parse(result.image_stored_only[0]) as PlainImageSection).attributes, AspectRatio.Standard),
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          if (isIrPressReleaseSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.News,
              title: getFirstValue(highlighting.headline, result.headline),
              content: getFirstValue(highlighting.abstract, highlighting.content, result.abstract),
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          if (isEventSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.Event,
              title: getFirstValue(highlighting.name, result.name),
              content: getFirstValue(highlighting.abstract, highlighting.content, result.abstract),
              picture: imageToPicture((JSON.parse(result.image_stored_only[0]) as PlainImageSection).attributes, AspectRatio.Standard),
              startDate: new Date(result.start_date),
              endDate: new Date(result.end_date),
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          if (isWebshopProductSearchResult(result)) {
            const mapped = {
              link: result.link,
              type: GlobalSearchResultType.Product,
              title: getFirstValue(result.partNumber),
              content: combineLines(
                getFirstValue(result.typeNumber_keyword),
                getFirstValue(highlighting.abstract, result.abstract),
                getFirstValue(result.status_keyword),
              ),
            } satisfies GlobalSearchResult as GlobalSearchResult;

            if (result.imageUrl_stored_only) {
              mapped.picture = urlToPicture(result.imageUrl_stored_only[0]);
            }

            return mapped;
          }

          if (isWebshopCrossReferenceSearchResult(result)) {
            const mapped = {
              link: result.link,
              type: GlobalSearchResultType.CrossReference,
              title: getFirstValue(result.internalPartNumber),
              content: combineLines(
                getFirstValue(result.typeNumber_keyword),
                getFirstValue(highlighting.title, result.title),
                getFirstValue(result.status_keyword),
                prefixValue($localize `:value prefix cross reference@@:Cross Reference`, getFirstValue(result.crossReferencePartNumber)),
                prefixValue($localize `:value prefix cross reference manufacturer@@:Manufacturer`, getFirstValue(result.crossReferenceManufacturer_keyword)),
              ),
            } satisfies GlobalSearchResult as GlobalSearchResult;

            if (result.imageUrl_stored_only) {
              mapped.picture = urlToPicture(result.imageUrl_stored_only[0]);
            }

            return mapped;
          }

          if (isWebshopDocumentSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.WebshopDocument,
              title: getFirstValue(highlighting.title, result.title),
              content: combineLines(
                getFirstValue(result.documentNumber),
                getFirstValue(result.category_keyword),
              ),
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          if (isWebshopSoftwareSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.Software,
              title: getFirstValue(result.softwarePackageName),
              content: combineLines(
                prefixValue($localize `:value prefix software group@@:Software Group`, getFirstValue(result.softwareGroup_keyword)),
                prefixValue($localize `:value prefix software type@@:Software Type`, getFirstValue(result.softwareType_keyword)),
                prefixValue($localize `:value prefix software package type@@:Software Package Type`, getFirstValue(result.softwarePackageType_keyword)),
              ),
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          if (isWebshopTrainingSearchResult(result)) {
            return {
              link: result.link,
              type: GlobalSearchResultType.Training,
              title: getFirstValue(highlighting.title, result.title),
              content: combineLines(
                prefixValue($localize `:value prefix training shortcode@@:Shortcode`, getFirstValue(result.trainingShortCode_keyword)),
                getFirstValue(highlighting.abstract, highlighting.content, result.abstract),
              ),
            } satisfies GlobalSearchResult as GlobalSearchResult;
          }

          this.logger.warning('Could not match search result type', {
            row: row,
          });

        } catch (error) {
          this.logger.warning('Failed to transform search result', {
            row: row,
            error: error,
          });
        }

        return undefined;

      }).filter((row): row is GlobalSearchResult => row !== undefined);
    }),
  );

  private destroy$ = new Subject<void>();

  private settings = inject(SettingsService);
  private titleSuffix = toSignal(this.settings.titleSuffix);

  public constructor() {
    inject(PageService).setSpecialPage();
    this.meta.updateTag({ name: 'robots', content: 'noindex' });
  }

  public ngOnInit() {
    const region = this.route.snapshot.paramMap.get('region');
    this.settings.setRegionCode(region);

    this.route.queryParamMap.pipe(
      takeUntil(this.destroy$),
    ).subscribe((paramMap) => {
      const query = paramMap.get(QUERY_PARAM_QUERY);
      if (query === null) {
        return;
      }
      const typeFilter = (paramMap.get(QUERY_PARAM_FILTER_TYPE) as HaupiaResponseType | null) ?? '';

      this.searchForm.controls.typeFilter.setValue(typeFilter, {
        emitEvent: false,
      });
      if (typeFilter) {
        this.preparedSearch.setFacetFilterManually('type', [typeFilter]);
      } else {
        this.preparedSearch.removeFacetFilter('type');
      }

      this.searchForm.controls.query.setValue(query, {
        emitEvent: false,
      });

      if( region){
        this.preparedSearch.setFacetFilterManually('countryCode', [region, GLOBAL_REGION] );
      } else {
        this.preparedSearch.removeFacetFilter('countryCode');
      }

      this.preparedSearch.setQuery(query);
    })

    combineLatest([
      this.searchForm.controls.query.valueChanges.pipe(
        startWith(this.searchForm.controls.query.value),
        debounceTime(800),
      ),
      this.searchForm.controls.typeFilter.valueChanges.pipe(
        startWith(this.searchForm.controls.typeFilter.value),
      ),
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([query, typeFilter]) => {

      this.router.navigate([], preventScroll({
        relativeTo: this.route,
        queryParams: {
          [QUERY_PARAM_QUERY]: query ? query : undefined,
          [QUERY_PARAM_FILTER_TYPE]: typeFilter ? typeFilter : undefined,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      }, this.platformBrowser));

      this.title.setTitle(`${query}${this.titleSuffix()}`)

      if (typeFilter) {
        this.preparedSearch.setFacetFilterManually('type', [typeFilter]);
      } else {
        this.preparedSearch.removeFacetFilter('type');
      }

      this.preparedSearch.setQuery(query);

    });


  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.preparedSearch.close();
  }
}
